import { z } from 'zod';

declare const __PLATO_DEPLOYMENT_PROFILE__: string | undefined;
declare const __PLATO_RELEASE_APPLICATION_VERSION__: string | undefined;
declare const __PLATO_RELEASE_COMMIT_SHA__: string | undefined;
declare const __PLATO_FRONTEND_RUNTIME_CONFIG_SHA256__: string | undefined;
declare const __PLATO_EXPECTED_BACKEND_CONFIG_FINGERPRINT__: string | undefined;

const SHA256_PATTERN = /^sha256:[a-f0-9]{64}$/;
const COMMIT_PATTERN = /^(?:[a-f0-9]{40}|[a-f0-9]{64})$/;
const GOOGLE_CLIENT_ID_PATTERN = /^[0-9]+-[a-z0-9]+\.apps\.googleusercontent\.com$/;
const FIREBASE_API_KEY_PATTERN = /^[A-Za-z0-9_-]{20,128}$/;
const FIREBASE_AUTH_DOMAIN_PATTERN = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;
const FIREBASE_APP_ID_PATTERN = /^1:[0-9]{6,20}:web:[a-f0-9]{8,64}$/;
const FIREBASE_PROJECT_ID_PATTERN = /^[a-z][a-z0-9-]{4,28}[a-z0-9]$/;
const FIREBASE_MESSAGING_SENDER_ID_PATTERN = /^[0-9]{6,20}$/;
const MAX_CONFIG_BYTES = 64 * 1024;

const frontendRuntimeConfigSchema = z.object({
  schemaVersion: z.literal('plato-frontend-runtime/v3'),
  deploymentProfile: z.literal('onprem-detached'),
  release: z.object({
    applicationVersion: z.string().trim().min(1).max(64),
    commitSha: z.string().regex(COMMIT_PATTERN),
  }).strict(),
  backend: z.object({
    deploymentProfile: z.literal('onprem-detached'),
    configFingerprint: z.string().regex(SHA256_PATTERN),
  }).strict(),
  auth: z.object({
    provider: z.literal('google'),
    googleClientId: z.string().regex(GOOGLE_CLIENT_ID_PATTERN),
    firebase: z.object({
      apiKey: z.string().regex(FIREBASE_API_KEY_PATTERN),
      authDomain: z.string().regex(FIREBASE_AUTH_DOMAIN_PATTERN),
      appId: z.string().regex(FIREBASE_APP_ID_PATTERN),
      projectId: z.string().regex(FIREBASE_PROJECT_ID_PATTERN),
      messagingSenderId: z.string().regex(FIREBASE_MESSAGING_SENDER_ID_PATTERN),
    }).strict(),
  }).strict(),
  api: z.object({
    baseUrl: z.literal('/api'),
    sseBaseUrl: z.literal('/api'),
  }).strict(),
}).strict();

export type FrontendRuntimeConfig = z.infer<typeof frontendRuntimeConfigSchema>;

export interface FrontendRuntimeConfigBootstrap {
  url: string;
  sha256: string;
  deploymentProfile: 'onprem-detached';
  applicationVersion: string;
  commitSha: string;
  backendConfigFingerprint: string;
}

interface RuntimeConfigGlobal extends Record<string, unknown> {
  __PLATO_RUNTIME_CONFIG_BOOTSTRAP__?: FrontendRuntimeConfigBootstrap;
  __VITE_API_URL__?: string;
  __VITE_AUTH_PROVIDER__?: string;
  __VITE_GOOGLE_CLIENT_ID__?: string;
  __VITE_FIREBASE_API_KEY__?: string;
  __VITE_FIREBASE_AUTH_DOMAIN__?: string;
  __VITE_FIREBASE_APP_ID__?: string;
  __VITE_FIREBASE_PROJECT_ID__?: string;
  __VITE_FIREBASE_MESSAGING_SENDER_ID__?: string;
  __PLATO_BACKEND_CONFIG_FINGERPRINT__?: string;
}

interface BuildTarget {
  deploymentProfile: string;
  applicationVersion?: string;
  commitSha?: string;
  frontendRuntimeConfigSha256?: string;
  backendConfigFingerprint?: string;
}

interface LoaderOptions {
  globalEnv?: RuntimeConfigGlobal;
  buildTarget?: BuildTarget;
  fetchImpl?: typeof fetch;
  digestSha256?: (content: Uint8Array) => Promise<string>;
  locationOrigin?: string;
  now?: () => number;
}

export type FrontendRuntimeConfigErrorCode =
  | 'RUNTIME_CONFIG_MISSING'
  | 'RUNTIME_CONFIG_SOURCE_INVALID'
  | 'RUNTIME_CONFIG_FETCH_FAILED'
  | 'RUNTIME_CONFIG_INTEGRITY_FAILED'
  | 'RUNTIME_CONFIG_SCHEMA_INVALID'
  | 'RUNTIME_CONFIG_INCOMPATIBLE'
  | 'RUNTIME_CONFIG_STALE';

export class FrontendRuntimeConfigError extends Error {
  constructor(public readonly publicCode: FrontendRuntimeConfigErrorCode, message: string) {
    super(message);
    this.name = 'FrontendRuntimeConfigError';
  }
}

function compileTimeValue(value: string | undefined): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function getBuildTarget(): BuildTarget {
  return {
    deploymentProfile: compileTimeValue(typeof __PLATO_DEPLOYMENT_PROFILE__ === 'undefined' ? undefined : __PLATO_DEPLOYMENT_PROFILE__) || 'azure-baseline',
    applicationVersion: compileTimeValue(typeof __PLATO_RELEASE_APPLICATION_VERSION__ === 'undefined' ? undefined : __PLATO_RELEASE_APPLICATION_VERSION__),
    commitSha: compileTimeValue(typeof __PLATO_RELEASE_COMMIT_SHA__ === 'undefined' ? undefined : __PLATO_RELEASE_COMMIT_SHA__),
    frontendRuntimeConfigSha256: compileTimeValue(typeof __PLATO_FRONTEND_RUNTIME_CONFIG_SHA256__ === 'undefined' ? undefined : __PLATO_FRONTEND_RUNTIME_CONFIG_SHA256__),
    backendConfigFingerprint: compileTimeValue(typeof __PLATO_EXPECTED_BACKEND_CONFIG_FINGERPRINT__ === 'undefined' ? undefined : __PLATO_EXPECTED_BACKEND_CONFIG_FINGERPRINT__),
  };
}

async function defaultDigestSha256(content: Uint8Array): Promise<string> {
  if (!globalThis.crypto?.subtle) {
    throw new FrontendRuntimeConfigError('RUNTIME_CONFIG_INTEGRITY_FAILED', 'SHA-256 is unavailable.');
  }
  const digest = await globalThis.crypto.subtle.digest('SHA-256', content as BufferSource);
  return `sha256:${Array.from(new Uint8Array(digest)).map((value) => value.toString(16).padStart(2, '0')).join('')}`;
}

function resolveBootstrap(globalEnv: RuntimeConfigGlobal, buildTarget: BuildTarget): FrontendRuntimeConfigBootstrap | undefined {
  if (buildTarget.deploymentProfile !== 'onprem-detached') {
    if (globalEnv.__PLATO_RUNTIME_CONFIG_BOOTSTRAP__) {
      throw new FrontendRuntimeConfigError('RUNTIME_CONFIG_SOURCE_INVALID', 'Runtime config bootstrap is forbidden for this build target.');
    }
    return undefined;
  }
  if (
    !buildTarget.applicationVersion
    || !buildTarget.commitSha
    || !buildTarget.frontendRuntimeConfigSha256
    || !buildTarget.backendConfigFingerprint
  ) {
    throw new FrontendRuntimeConfigError('RUNTIME_CONFIG_MISSING', 'On-prem build target is missing pinned runtime config metadata.');
  }
  const pinnedBootstrap: FrontendRuntimeConfigBootstrap = {
    url: '/.well-known/plato-runtime-config.json',
    sha256: buildTarget.frontendRuntimeConfigSha256,
    deploymentProfile: 'onprem-detached',
    applicationVersion: buildTarget.applicationVersion,
    commitSha: buildTarget.commitSha,
    backendConfigFingerprint: buildTarget.backendConfigFingerprint,
  };
  const injectedBootstrap = globalEnv.__PLATO_RUNTIME_CONFIG_BOOTSTRAP__;
  if (!injectedBootstrap) {
    return pinnedBootstrap;
  }
  if (
    injectedBootstrap.deploymentProfile !== pinnedBootstrap.deploymentProfile
    || injectedBootstrap.applicationVersion !== pinnedBootstrap.applicationVersion
    || injectedBootstrap.commitSha !== pinnedBootstrap.commitSha
    || injectedBootstrap.sha256 !== pinnedBootstrap.sha256
    || injectedBootstrap.backendConfigFingerprint !== pinnedBootstrap.backendConfigFingerprint
  ) {
    throw new FrontendRuntimeConfigError('RUNTIME_CONFIG_SOURCE_INVALID', 'Injected runtime config metadata does not match immutable build pins.');
  }
  return { ...pinnedBootstrap, url: injectedBootstrap.url };
}

function validateBootstrap(bootstrap: FrontendRuntimeConfigBootstrap, locationOrigin: string): URL {
  if (
    bootstrap.deploymentProfile !== 'onprem-detached'
    || !bootstrap.applicationVersion?.trim()
    || !COMMIT_PATTERN.test(bootstrap.commitSha)
    || !SHA256_PATTERN.test(bootstrap.sha256)
    || !SHA256_PATTERN.test(bootstrap.backendConfigFingerprint)
  ) {
    throw new FrontendRuntimeConfigError('RUNTIME_CONFIG_SOURCE_INVALID', 'Runtime config bootstrap metadata is invalid.');
  }
  let source: URL;
  try {
    source = new URL(bootstrap.url, locationOrigin);
  } catch {
    throw new FrontendRuntimeConfigError('RUNTIME_CONFIG_SOURCE_INVALID', 'Runtime config URL is invalid.');
  }
  if (source.origin !== locationOrigin || source.username || source.password || source.search || source.hash) {
    throw new FrontendRuntimeConfigError('RUNTIME_CONFIG_SOURCE_INVALID', 'Runtime config must be fetched from an exact same-origin path.');
  }
  return source;
}

function assertCompatible(config: FrontendRuntimeConfig, bootstrap: FrontendRuntimeConfigBootstrap): void {
  if (
    config.deploymentProfile !== bootstrap.deploymentProfile
    || config.backend.deploymentProfile !== bootstrap.deploymentProfile
    || config.release.applicationVersion !== bootstrap.applicationVersion
    || config.release.commitSha !== bootstrap.commitSha
    || config.backend.configFingerprint !== bootstrap.backendConfigFingerprint
  ) {
    throw new FrontendRuntimeConfigError('RUNTIME_CONFIG_INCOMPATIBLE', 'Runtime config does not match the pinned release or backend profile.');
  }
}

export async function initializeFrontendRuntimeConfig(options: LoaderOptions = {}): Promise<FrontendRuntimeConfig | undefined> {
  const globalEnv = options.globalEnv ?? globalThis as RuntimeConfigGlobal;
  const buildTarget = options.buildTarget ?? getBuildTarget();
  const bootstrap = resolveBootstrap(globalEnv, buildTarget);
  if (!bootstrap) {
    return undefined;
  }
  const locationOrigin = options.locationOrigin ?? globalThis.location?.origin;
  if (!locationOrigin) {
    throw new FrontendRuntimeConfigError('RUNTIME_CONFIG_SOURCE_INVALID', 'Browser origin is unavailable.');
  }
  const source = validateBootstrap(bootstrap, locationOrigin);
  let response: Response;
  try {
    response = await (options.fetchImpl ?? fetch)(source.toString(), {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      redirect: 'error',
      headers: { Accept: 'application/json' },
    });
  } catch {
    throw new FrontendRuntimeConfigError('RUNTIME_CONFIG_FETCH_FAILED', 'Runtime config request failed.');
  }
  if (!response.ok || !response.headers.get('content-type')?.toLowerCase().includes('application/json')) {
    throw new FrontendRuntimeConfigError('RUNTIME_CONFIG_FETCH_FAILED', 'Runtime config response was not an accepted JSON document.');
  }
  const content = new TextEncoder().encode(await response.text());
  if (content.byteLength === 0 || content.byteLength > MAX_CONFIG_BYTES) {
    throw new FrontendRuntimeConfigError('RUNTIME_CONFIG_SCHEMA_INVALID', 'Runtime config document size is invalid.');
  }
  const actualDigest = await (options.digestSha256 ?? defaultDigestSha256)(content);
  if (actualDigest !== bootstrap.sha256) {
    throw new FrontendRuntimeConfigError('RUNTIME_CONFIG_INTEGRITY_FAILED', 'Runtime config content digest does not match the pinned digest.');
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(new TextDecoder().decode(content));
  } catch {
    throw new FrontendRuntimeConfigError('RUNTIME_CONFIG_SCHEMA_INVALID', 'Runtime config is not valid JSON.');
  }
  const result = frontendRuntimeConfigSchema.safeParse(parsed);
  if (!result.success) {
    throw new FrontendRuntimeConfigError('RUNTIME_CONFIG_SCHEMA_INVALID', 'Runtime config does not match plato-frontend-runtime/v3.');
  }
  assertCompatible(result.data, bootstrap);

  globalEnv.__VITE_API_URL__ = result.data.api.baseUrl;
  globalEnv.__VITE_AUTH_PROVIDER__ = result.data.auth.provider;
  globalEnv.__VITE_GOOGLE_CLIENT_ID__ = result.data.auth.googleClientId;
  globalEnv.__VITE_FIREBASE_API_KEY__ = result.data.auth.firebase.apiKey;
  globalEnv.__VITE_FIREBASE_AUTH_DOMAIN__ = result.data.auth.firebase.authDomain;
  globalEnv.__VITE_FIREBASE_APP_ID__ = result.data.auth.firebase.appId;
  globalEnv.__VITE_FIREBASE_PROJECT_ID__ = result.data.auth.firebase.projectId;
  globalEnv.__VITE_FIREBASE_MESSAGING_SENDER_ID__ = result.data.auth.firebase.messagingSenderId;
  globalEnv.__PLATO_BACKEND_CONFIG_FINGERPRINT__ = result.data.backend.configFingerprint;
  return result.data;
}
